import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { XLogo, LinkedinLogo, EnvelopeSimple, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const COLS = [
  {
    title: "Shop",
    links: [
      { to: "/shop", label: "All Compounds" },
      { to: "/quality", label: "Quality & Testing" },
      { to: "/blog", label: "Blog", testid: "footer-link-blog" },
    ],
  },
  {
    title: "Account",
    links: [
      { to: "/login", label: "Login" },
      { to: "/register", label: "Create Account" },
      { to: "/account", label: "My Orders" },
      { to: "/order-tracking", label: "Order Tracking" },
    ],
  },
  {
    title: "Support",
    links: [
      { to: "/contact", label: "Contact" },
      { to: "/faq", label: "FAQ" },
      { to: "/shipping-policy", label: "Shipping Policy" },
      { to: "/returns", label: "Final-Sale & Claims" },
    ],
  },
  {
    title: "Legal",
    links: [
      { to: "/regulatory-disclaimer", label: "Regulatory Disclaimer" },
      { to: "/research-use-policy", label: "Research Use Policy" },
      { to: "/warranty", label: "Warranty Disclaimer" },
      { to: "/terms", label: "Terms & Conditions" },
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/state-restrictions", label: "State Restrictions" },
    ],
  },
];

const HOURS = [
  { d: "Monday – Friday", h: "8:00 AM – 6:00 PM ET" },
  { d: "Saturday",        h: "10:00 AM – 2:00 PM ET" },
  { d: "Sunday",          h: "Closed" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const location = useLocation();

  const subscribe = (e) => {
    e.preventDefault();
    if (!/.+@.+\..+/.test(email)) { toast.error("Please enter a valid email address"); return; }
    toast.success("Thanks — we'll add you to our research bulletin.");
    setEmail("");
  };

  // Eligibility-gate routes must NOT expose the Shop / Categories / Wholesale
  // navigation until the visitor has affirmed research-use eligibility. Serve
  // a minimal legal-only footer for those two routes.
  const isEligibilityRoute =
    location.pathname === "/research-eligibility" ||
    location.pathname === "/research-eligibility/declined";

  if (isEligibilityRoute) {
    return (
      <footer className="bg-brand-navy text-white mt-24" data-testid="site-footer-minimal">
        <div className="mps-container py-10 text-white/70 text-xs">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              © {new Date().getFullYear()} Medical Peptide Store — Research reference standards.
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              <Link to="/" className="hover:text-brand-accent transition-colors" data-testid="minfooter-link-home">Home</Link>
              <Link to="/research-use-policy" className="hover:text-brand-accent transition-colors" data-testid="minfooter-link-research-use-policy">Research-Use Policy</Link>
              <Link to="/privacy" className="hover:text-brand-accent transition-colors" data-testid="minfooter-link-privacy">Privacy Policy</Link>
              <Link to="/contact" className="hover:text-brand-accent transition-colors" data-testid="minfooter-link-contact">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer
      className="bg-brand-navy text-white mt-24"
      data-testid="site-footer"
    >
      <div className="mps-container py-16 lg:py-20">
        {/* Newsletter row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-14 border-b border-white/10">
          <div className="lg:col-span-6">
            <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-accent mb-3">Research Bulletin</div>
            <h3 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
              Compound releases, batch documentation & quality notes.
            </h3>
            <p className="mt-3 text-white/70 text-base leading-relaxed max-w-lg">
              A concise monthly note — new reference standards, quality-testing updates, and shipping notices. Zero marketing.
            </p>
          </div>
          <form onSubmit={subscribe} className="lg:col-span-6 flex flex-col sm:flex-row items-stretch gap-3 self-end w-full" data-testid="footer-newsletter" data-clarity-mask="true">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@lab.example"
              data-testid="footer-newsletter-input"
              className="flex-1 h-14 rounded-[14px] bg-white/10 border border-white/15 px-5 text-white placeholder:text-white/40 focus:outline-none focus:border-brand-accent focus:bg-white/15 transition-colors"
            />
            <button
              type="submit"
              data-testid="footer-newsletter-submit"
              className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-[14px] bg-brand-accent hover:bg-[#1E6BD8] text-white font-semibold transition-colors"
            >
              Subscribe <ArrowRight size={16} weight="bold" />
            </button>
          </form>
        </div>

        {/* Main columns */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10 pt-14">
          <div className="col-span-2 md:col-span-2">
            <Link
              to="/"
              data-testid="footer-brand-logo"
              className="inline-block group"
              aria-label="Medical Peptide Store — home"
            >
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_tender-goldberg-9/artifacts/1kwkf9ch_Generated%20image%201%20%285%29.png"
                alt="Medical Peptide Store"
                className="block h-auto w-[210px] md:w-[240px] object-contain group-hover:opacity-90 transition-opacity"
                loading="lazy"
                decoding="async"
              />
            </Link>
            <p className="mt-5 text-sm text-white/70 leading-relaxed max-w-sm" data-testid="footer-company-blurb">
              A U.S.-based supplier of research reference standards, with all peptides manufactured in the United States. Every order ships signature-required with lot-tracked cartons. Lot-specific documentation is reviewed internally and available on request.
            </p>
            <div className="mt-6 space-y-2 text-sm">
              <a href="mailto:info@medicalpeptidestore.com" data-testid="footer-email" className="block text-white/80 hover:text-brand-accent transition-colors">
                info@medicalpeptidestore.com
              </a>
              <a href="tel:+18882947717" data-testid="footer-phone" className="block text-white/80 hover:text-brand-accent transition-colors">
                (888) 294-7717
              </a>
            </div>
            <div className="mt-6">
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/40 mb-2">Support Hours</div>
              <ul className="space-y-1 text-xs text-white/70">
                {HOURS.map(({ d, h }) => (
                  <li key={d} className="flex items-center justify-between max-w-[220px]">
                    <span>{d}</span>
                    <span className="text-white/50">{h}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <a href="#" className="h-9 w-9 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white/80 hover:text-brand-accent hover:border-brand-accent transition-colors" aria-label="X">
                <XLogo size={15} weight="regular" />
              </a>
              <a href="#" className="h-9 w-9 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white/80 hover:text-brand-accent hover:border-brand-accent transition-colors" aria-label="LinkedIn">
                <LinkedinLogo size={15} weight="regular" />
              </a>
              <a href="mailto:info@medicalpeptidestore.com" className="h-9 w-9 rounded-full bg-white/10 border border-white/15 flex items-center justify-center text-white/80 hover:text-brand-accent hover:border-brand-accent transition-colors" aria-label="Email">
                <EnvelopeSimple size={15} weight="regular" />
              </a>
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/40 mb-5">{col.title}</div>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      data-testid={l.testid}
                      className="text-sm text-white/80 hover:text-brand-accent transition-colors duration-200"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* RUO strip */}
        <div className="mt-14 pt-8 border-t border-white/10" data-testid="footer-ruo-disclaimer">
          <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-brand-accent mb-3">Regulatory Notice</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[11px] font-medium uppercase tracking-[0.14em] text-white/70">
            <div>For research use only</div>
            <div>Not for human or veterinary use</div>
            <div>Not intended to diagnose, treat, cure or prevent any disease</div>
          </div>
        </div>

        <div className="mt-8 flex flex-col md:flex-row md:items-center justify-between gap-3 text-xs text-white/50">
          <span>© {new Date().getFullYear()} Medical Peptide Store. All rights reserved.</span>
          <span className="max-w-md md:text-right">
            Research reference standards for laboratory use. Not offered as a consumer drug, food, dietary supplement, cosmetic, or medical intervention.
          </span>
        </div>
      </div>
    </footer>
  );
};
